import { useEffect, useState } from 'react';
import type { CustomerStatementResponse, LegacyDocumentDetail } from '@hesabat/contracts';

/**
 * كشف عميل أو مورد بشكل واحد — الطرف فى `party` بدل `customer`/`supplier`. حساب الدفعات
 * المقدمة وحساب كل سطر للعميل وحده، فهما اختياريان هنا.
 */
type Statement = Omit<CustomerStatementResponse, 'customer' | 'advanceAccountCode' | 'lines'> & {
  party: CustomerStatementResponse['customer'];
  advanceAccountCode?: string;
  lines: (Omit<CustomerStatementResponse['lines'][number], 'accountCode'> & {
    accountCode?: string;
  })[];
};
import { SOURCE_LABELS } from './AccountLedger';
import { api, legacyApi, ProblemError, UnauthorizedError } from './api';
import { downloadCsv } from './csv';
import { formatMoney, isZero } from './money';
import LegacyArchive from './LegacyArchive';
import { legacyDocument, PrintPreview, type PrintableDocument } from './PrintDocument';

type Line = Statement['lines'][number];

const documentOf = (line: Line) =>
  line.journalId ? (line.journalNumber ?? line.journalId.slice(0, 8)) : 'أرشيف';

/** الكشف بشكل الفاتورة: نفس الترويسة واللوجو والتوقيعات، والأصناف تحت مستندها لو مفصّل. */
function statementDocument(
  statement: Statement,
  kind: 'customer' | 'supplier',
  currency: string,
  detailed: boolean,
): PrintableDocument {
  const closing = statement.closingBalance;
  const owes =
    kind === 'customer'
      ? closing.startsWith('-')
        ? 'رصيد دائن للعميل'
        : 'المستحق على العميل'
      : closing.startsWith('-')
        ? 'رصيد مدين على المورد'
        : 'المستحق للمورد';
  return {
    title: kind === 'customer' ? 'كشف حساب عميل' : 'كشف حساب مورد',
    number: statement.party.code,
    fields: [
      { label: 'من', value: statement.from },
      { label: 'إلى', value: statement.to },
      { label: 'تاريخ الطباعة', value: new Date().toISOString().slice(0, 10) },
    ],
    party: {
      label: kind === 'customer' ? 'العميل' : 'المورد',
      name: statement.party.nameAr,
      details: ['الكود: ' + statement.party.code],
    },
    currency,
    lines: null,
    statement: {
      from: statement.from,
      opening: statement.openingBalance,
      rows: statement.lines.map((line) => ({
        date: line.postingDate,
        document: line.reference ?? documentOf(line),
        memo: line.memo ?? SOURCE_LABELS[line.sourceType] ?? '',
        debit: line.debit,
        credit: line.credit,
        balance: line.runningBalance,
        items: detailed
          ? line.items.map((item) => ({
              name: [item.code, item.name].filter(Boolean).join(' — '),
              quantity: item.quantity ?? '',
              unitPrice: item.unitPrice ?? '',
              total: item.total ?? '',
            }))
          : [],
      })),
      to: statement.to,
      totalDebit: statement.totalDebit,
      totalCredit: statement.totalCredit,
      closing,
    },
    totals: [
      { label: 'إجمالى المدين', value: statement.totalDebit },
      { label: 'إجمالى الدائن', value: statement.totalCredit },
      { label: owes, value: closing.replace(/^-/, ''), grand: true },
    ],
    amountInWords: closing.replace(/^-/, ''),
    note: null,
    cancelled: false,
    showFooter: false,
    eInvoice: null,
  };
}

interface Props {
  companyId: string;
  kind: 'customer' | 'supplier';
  partyId: string;
  currency: string;
  onSessionLost: () => void;
}

function yearBounds(): { from: string; to: string } {
  const year = new Date().getFullYear();
  return { from: year + '-01-01', to: year + '-12-31' };
}

/**
 * كشف حساب عميل أو مورد — من الدفتر لا من المستندات: كل سطر على الحساب الرقابى يحمل
 * معرّف الطرف. العميل بطبيعة مدينة (الموجب مستحق عليه)، والمورد بطبيعة دائنة (الموجب مستحق له).
 */
export default function PartyStatement({
  companyId,
  kind,
  partyId,
  currency,
  onSessionLost,
}: Props) {
  const [period, setPeriod] = useState(yearBounds);
  const [statement, setStatement] = useState<Statement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detailed, setDetailed] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [legacy, setLegacy] = useState<LegacyDocumentDetail | null>(null);

  async function openLegacy(documentId: string): Promise<void> {
    try {
      setLegacy(await legacyApi.get(companyId, documentId));
    } catch (caught) {
      if (caught instanceof UnauthorizedError) onSessionLost();
      else setError(caught instanceof Error ? caught.message : String(caught));
    }
  }

  useEffect(() => {
    let cancelled = false;
    setError(null);
    if (period.from > period.to) {
      setError('تاريخ البداية بعد تاريخ النهاية.');
      return;
    }
    const load: Promise<Statement> =
      kind === 'customer'
        ? api
            .customerStatement(companyId, partyId, period.from, period.to)
            .then(({ customer, ...rest }) => ({ ...rest, party: customer }))
        : api
            .supplierStatement(companyId, partyId, period.from, period.to)
            .then(({ supplier, ...rest }) => ({ ...rest, party: supplier }));
    load
      .then((result) => {
        if (!cancelled) setStatement(result);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof UnauthorizedError) onSessionLost();
        else if (caught instanceof ProblemError)
          setError(caught.problem.detail ?? caught.problem.title);
        else setError(caught instanceof Error ? caught.message : String(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, kind, partyId, period, onSessionLost]);

  function exportCsv(): void {
    if (!statement) return;
    downloadCsv(
      (kind === 'customer' ? 'كشف-عميل-' : 'كشف-مورد-') +
        statement.party.code +
        '-' +
        statement.from +
        '-' +
        statement.to +
        '.csv',
      ['التاريخ', 'القيد', 'النوع', 'المرجع', 'البيان', 'مدين', 'دائن', 'الرصيد'],
      [
        [statement.from, '', '', '', 'رصيد افتتاحى', '', '', statement.openingBalance],
        ...statement.lines.map((line) => [
          line.postingDate,
          line.journalNumber ?? line.journalId,
          SOURCE_LABELS[line.sourceType] ?? line.sourceType,
          line.reference,
          line.memo,
          line.debit,
          line.credit,
          line.runningBalance,
        ]),
        [
          statement.to,
          '',
          '',
          '',
          'الإجمالى والرصيد الختامى',
          statement.totalDebit,
          statement.totalCredit,
          statement.closingBalance,
        ],
      ],
    );
  }

  return (
    <>
      <div className="panel">
        <div className="panel-head">
          <h2>
            كشف حساب{' '}
            {statement
              ? statement.party.code + ' — ' + statement.party.nameAr
              : kind === 'customer'
                ? 'عميل'
                : 'مورد'}
          </h2>
          {statement && (
            <div className="panel-actions no-print">
              <button type="button" className="ghost" onClick={exportCsv}>
                تصدير Excel
              </button>
              <button type="button" className="primary" onClick={() => setPrinting(true)}>
                طباعة / PDF
              </button>
            </div>
          )}
        </div>
        <div className="form-row">
          <label>
            من
            <input
              type="date"
              value={period.from}
              onChange={(event) =>
                setPeriod((current) => ({ ...current, from: event.target.value }))
              }
            />
          </label>
          <label>
            إلى
            <input
              type="date"
              value={period.to}
              onChange={(event) => setPeriod((current) => ({ ...current, to: event.target.value }))}
            />
          </label>
          {statement?.firstActivityDate && statement.firstActivityDate < period.from && (
            <button
              type="button"
              className="ghost"
              onClick={() =>
                setPeriod((current) => ({ ...current, from: statement.firstActivityDate! }))
              }
            >
              من أول التعامل ({statement.firstActivityDate})
            </button>
          )}
          <label className="check">
            <input
              type="checkbox"
              checked={detailed}
              onChange={(event) => setDetailed(event.target.checked)}
            />
            مفصّل بالأصناف
          </label>
        </div>
        {error && <p className="error">{error}</p>}
        {statement && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>القيد</th>
                  <th>النوع</th>
                  <th>المرجع</th>
                  <th>البيان</th>
                  <th>مدين</th>
                  <th>دائن</th>
                  <th>الرصيد</th>
                </tr>
              </thead>
              <tbody>
                <tr className="opening">
                  <td>{statement.from}</td>
                  <td colSpan={6}>رصيد افتتاحى</td>
                  <td className="num-cell">{formatMoney(statement.openingBalance)}</td>
                </tr>
                {statement.lines.map((line, index) => (
                  <StatementLine
                    key={
                      (line.journalId ?? line.legacyDocumentId) +
                      ':' +
                      line.lineNumber +
                      ':' +
                      index
                    }
                    line={line}
                    detailed={detailed}
                    onOpen={
                      line.legacyDocumentId ? () => void openLegacy(line.legacyDocumentId!) : null
                    }
                  />
                ))}
                {statement.lines.length === 0 && (
                  <tr>
                    <td colSpan={8} className="sub">
                      لا حركة على هذا الطرف فى هذه الفترة.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="closing">
                  <td>{statement.to}</td>
                  <td colSpan={4}>الإجمالى والرصيد الختامى</td>
                  <td className="num-cell total">{formatMoney(statement.totalDebit)}</td>
                  <td className="num-cell total">{formatMoney(statement.totalCredit)}</td>
                  <td className="num-cell total">{formatMoney(statement.closingBalance)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <p className="label">
          المبالغ بـ {currency}.{' '}
          {kind === 'customer'
            ? 'الموجب مستحق على العميل، والسالب دائن له (تحصيل لم يُخصص).'
            : 'الموجب مستحق للمورد، والسالب مدفوع له مقدمًا (سداد لم يُخصص).'}
        </p>
      </div>
      {printing && statement && (
        <PrintPreview
          companyId={companyId}
          doc={statementDocument(statement, kind, currency, detailed)}
          onClose={() => setPrinting(false)}
          onSessionLost={onSessionLost}
        />
      )}
      {legacy && (
        <PrintPreview
          companyId={companyId}
          doc={legacyDocument(legacy, currency)}
          onClose={() => setLegacy(null)}
          onSessionLost={onSessionLost}
        />
      )}
      <LegacyArchive
        companyId={companyId}
        currency={currency}
        party={{ type: kind, id: partyId }}
        onSessionLost={onSessionLost}
      />
    </>
  );
}

/** سطر الكشف، ومستند البرنامج القديم يفتح بالضغط، والأصناف تحته لو مفصّل. */
function StatementLine({
  line,
  detailed,
  onOpen,
}: {
  line: Line;
  detailed: boolean;
  onOpen: (() => void) | null;
}) {
  return (
    <>
      <tr
        className={onOpen ? 'clickable' : undefined}
        onClick={onOpen ?? undefined}
        title={onOpen ? 'اضغط لعرض المستند وطباعته' : undefined}
      >
        <td>{line.postingDate}</td>
        <td className="mono">{documentOf(line)}</td>
        <td>
          {line.journalId ? (SOURCE_LABELS[line.sourceType] ?? line.sourceType) : 'البرنامج القديم'}
        </td>
        <td className="mono">{line.reference ?? ''}</td>
        <td>{line.memo ?? ''}</td>
        <td className="num-cell">{isZero(line.debit) ? '' : formatMoney(line.debit)}</td>
        <td className="num-cell">{isZero(line.credit) ? '' : formatMoney(line.credit)}</td>
        <td className="num-cell">{formatMoney(line.runningBalance)}</td>
      </tr>
      {detailed &&
        line.items.map((item, index) => (
          <tr key={index} className="statement-item">
            <td colSpan={4} />
            <td>
              {[item.code, item.name].filter(Boolean).join(' — ')}
              {item.quantity && (
                <span className="label">
                  {' '}
                  {item.quantity.includes('.')
                    ? item.quantity.replace(/.?0+$/, '')
                    : item.quantity}{' '}
                  × {formatMoney(item.unitPrice || '0')}
                </span>
              )}
            </td>
            <td className="num-cell">{item.total ? formatMoney(item.total) : ''}</td>
            <td colSpan={2} />
          </tr>
        ))}
    </>
  );
}
